import { Document, Image, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Order } from '../../types/orders';
import logo from "src/images/nhwm_logo.png";
import signature from "src/images/signature.png";

const styles = StyleSheet.create({
  page: {
    paddingTop: 18,
    paddingHorizontal: 20,
    paddingBottom: 16,
    fontSize: 8,
    fontFamily: "Helvetica",
  },

  // ================= HEADER =================

  header: {
    flexDirection: "row",
    marginBottom: 6,
    borderBottomWidth: 1,
    paddingBottom: 4,
  },

  logoSection: {
    flexDirection: "row",
    gap: 10,
    width: "50%",
  },

  logo: {
    width: 480,
    height: 50,
    objectFit: "contain",
  },

  companyName: {
    fontSize: 16,
    fontWeight: "bold",
    color: "#1995d3",
    lineHeight: 1.3,
    marginTop: 8,
  },

  companyInfo: {
    width: "50%",
    alignItems: "flex-start",
  },

  companyTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 2,
    textAlign: "center",
  },

  companyText: {
    fontSize: 7,
    lineHeight: 1.2,
    textAlign: "center",
  },

  invoiceTitle: {
    fontSize: 12,
    textAlign: "center",
    marginVertical: 5,
    fontWeight: "bold",
  },

  // ================= TOP INFO =================
  topSection: {
    flexDirection: "row",
    justifyContent: "space-between",
    borderWidth: 1,
    marginBottom: 4,
  },

  leftTop: {
    width: "50%",
    borderRight: "1 solid #000",
    padding: 8,
    minHeight: 80,
  },

  rightTop: {
    width: "50%",
    padding: 8,
    alignItems: "center",
  },

  infoText: {
    fontSize: 7,
    marginBottom: 1,
  },

  qrTitle: {
    fontSize: 8,
    marginBottom: 5,
  },

  qrImage: {
    width: 80,
    height: 80,
  },

  // ================= ADDRESS SECTION =================
  addressWrapper: {
    flexDirection: "row",
    borderWidth: 1,
    marginBottom: 5,
  },

  addressBox: {
    flex: 1,
    padding: 5,
  },

  addressDivider: {
    borderRight: "1 solid #000",
  },

  addressTitle: {
    fontSize: 8,
    fontWeight: "bold",
    marginBottom: 2,
  },

  addressText: {
    fontSize: 7,
    lineHeight: 1.2,
  },

  // ================= TABLE =================

  tableWrapper: {
    borderWidth: 1.2,
    borderColor: "#000",
    marginTop: 14,
  },

  tableHeader: {
    flexDirection: "row",
    minHeight: 20,
    backgroundColor: "#f2f2f2",
  },

  tableRow: {
    flexDirection: "row",
    minHeight: 18,
  },

  cell: {
    paddingVertical: 2,
    paddingHorizontal: 3,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    justifyContent: "center",
  },

  headerCell: {
    justifyContent: "center",
    alignItems: "center",
  },

  descCol: { flex: 45 },
  priceCol: { flex: 20, alignItems: "center" },
  qtyCol: { flex: 15, alignItems: "center" },
  amountCol: { flex: 16, alignItems: "center", borderRightWidth: 0 },

  headerTitle: {
    fontSize: 7,
    fontWeight: "bold",
    textAlign: "center",
  },

  headerSubTitle: {
    fontSize: 5.5,
    textAlign: "center",
  },

  bodyText: {
    fontSize: 6.5,
  },

  centerText: {
    fontSize: 6.5,
    textAlign: "center",
  },

  discountRow: {
    flexDirection: "row",
    minHeight: 10,
    borderBottomWidth: 1,
    borderColor: "#000",
  },

  discountText: {
    fontSize: 8,
    fontWeight: 500,
    marginVertical: 2,
  },

  discountAmountText: {
    fontSize: 8,
    fontWeight: 500,
    marginVertical: 2,
  },

  shippingChargeRow: {
    flexDirection: "row",
    minHeight: 10,
    borderBottomWidth: 1,
    borderColor: "#000",
  },

  shippingChargeText: {
    fontSize: 8,
    marginVertical: 2,
    fontWeight: 500,
  },

  shippingChargeAmountText: {
    marginVertical: 2,
    fontSize: 8,
    fontWeight: 500,
  },

  totalRow: {
    flexDirection: "row",
    minHeight: 20,
  },

  totalPayableText: {
    fontSize: 10,
    fontWeight: 700,
  },

  totalAmountText: {
    fontSize: 10,
    fontWeight: 700,
  },

  bigTotalAmount: {
    fontSize: 10,
    fontWeight: "bold",
  },

  // ================= FOOTER =================
  footer: {
    marginTop: 5,
    borderTopWidth: 1,
    paddingTop: 4,
  },

  footerHeading: {
    fontSize: 7,
    textAlign: "center",
    marginBottom: 4,
  },

  termsWrapper: {
    flexDirection: "row",
    gap: 8,
  },

  term: {
    fontSize: 6,
    lineHeight: 1.2,
    marginBottom: 2,
  },

  signatureImage: {
    width: 70,
    height: 28,
    objectFit: "contain",
  },

  signatureLine1: {
    fontSize: 6.5,
  },

  signatureName: {
    fontSize: 6.5,
  },

  preparedBy: {
    fontSize: 6,
  },

  termsColumn: {
    width: "48%",
  },

  suggestion: {
    marginTop: 8,
    fontSize: 8,
    fontWeight: "bold",
  },

  suggestionBlue: {
    color: "blue",
  },

  signatureSection: {
    marginTop: 30,
    alignItems: "flex-end",
  },
});

interface InvoicePDFProps {
  order: Order & {
    qr_code?: string;
  };
}

export const InvoicePDF = ({ order }: InvoicePDFProps) => (
  <Document>
    <Page size="A4" style={styles.page}>
      {/* HEADER */}
      <View style={styles.header} wrap={false}>
        <View style={styles.logoSection}>
          <Image style={styles.logo} src={logo} />
        </View>

        <View style={styles.companyInfo}>
          <Text style={styles.companyTitle}>
            National Healthcare and Wellness Mission
          </Text>

          <Text style={styles.companyText}>
            Registered Office :- 01, Karb Namghar Path, Barbari, Guwahati
          </Text>

          <Text style={styles.companyText}>
            Kamrup metro, ASSAM, 781036 - India
          </Text>

          <Text style={styles.companyText}>Contact No. - 9707076232</Text>

          <Text style={styles.companyText}>
            Email - nhwm.info@gmail.com Website - www.nhwm.in
          </Text>
        </View>
      </View>

      <Text style={styles.invoiceTitle}>INVOICE</Text>

      {/* TOP SECTION */}
      <View style={styles.topSection}>
        <View style={styles.leftTop}>
          <Text style={styles.infoText}>Order No : {order.order_number}</Text>

          <Text style={styles.infoText}>
            Order Date :{" "}
            {new Date(order.created_at).toLocaleDateString("en-IN")}
          </Text>

          <Text style={styles.infoText}>
            Invoice Date : {new Date().toLocaleDateString("en-IN")}
          </Text>

          <Text style={styles.infoText}>Mode of Transport : Courier</Text>

          <Text style={styles.infoText}>FSSAI Reg. No : 20326101000835</Text>

          <Text style={styles.infoText}>Supplement Brands : Vestige</Text>
        </View>

        <View style={styles.rightTop}>
          <Text style={styles.qrTitle}>
            Scan for Customer Detail Authentication.
          </Text>

          {order.qr_code && (
            <Image
              src={order.qr_code}
              style={{
                width: 60,
                height: 60,
              }}
            />
          )}
        </View>
      </View>

      {/* ADDRESS SECTION */}
      <View style={styles.addressWrapper}>
        <View style={[styles.addressBox, styles.addressDivider]}>
          <Text style={styles.addressTitle}>
            Details Of Receiver (Billed To)
          </Text>

          <Text style={styles.addressText}>Name : {order.address.name}</Text>

          <Text style={styles.addressText}>
            Address : {order.address.addressLine}, {order.address.landMark},{" "}
            {order.address.city}, Pin Code: {order.address.postalCode}, State:{" "}
            {order.address.state}
          </Text>
          <Text style={styles.addressText}>Phone: {order.address.phone}</Text>
        </View>

        <View style={styles.addressBox}>
          <Text style={styles.addressTitle}>
            Details Of Consignee (Shipped To)
          </Text>

          <Text style={styles.addressText}>Name : {order.address.name}</Text>

          <Text style={styles.addressText}>
            Address : {order.address.addressLine}, {order.address.landMark},{" "}
            {order.address.city}, Pin Code: {order.address.postalCode}, State:{" "}
            {order.address.state}
          </Text>
          <Text style={styles.addressText}>Phone: {order.address.phone}</Text>
        </View>
      </View>

      {/* TABLE */}
      <View style={styles.tableWrapper}>
        <View style={{ flexDirection: "row" }}>
          {/* ── LEFT: header + rows + total all share ONE flex parent ── */}
          <View style={{ flex: 96 }} wrap={false}>
            {/* HEADER */}
            <View style={styles.tableHeader}>
              <View style={[styles.cell, styles.descCol, styles.headerCell]}>
                <Text style={styles.headerTitle}>Product Description</Text>
              </View>
              <View style={[styles.cell, styles.priceCol, styles.headerCell]}>
                <Text style={styles.headerTitle}>Unit Price (MRP)</Text>
              </View>
              <View style={[styles.cell, styles.qtyCol, styles.headerCell]}>
                <Text style={styles.headerTitle}>Quantity</Text>
              </View>
              <View style={[styles.cell, styles.amountCol, styles.headerCell]}>
                <Text style={styles.headerTitle}>Amount</Text>
              </View>
            </View>

            {/* BODY ROWS */}
            {order.order_items?.map((item, index) => (
              <View key={index} style={styles.tableRow}>
                <View style={[styles.cell, styles.descCol]}>
                  <Text style={styles.bodyText}>{item.productName}</Text>
                </View>
                <View style={[styles.cell, styles.priceCol]}>
                  <Text style={styles.centerText}>{item.price}</Text>
                </View>
                <View style={[styles.cell, styles.qtyCol]}>
                  <Text style={styles.centerText}>{item.quantity}</Text>
                </View>
                <View style={[styles.cell, styles.amountCol]}>
                  <Text style={styles.centerText}>
                    {(Number(item.price) * item.quantity).toFixed(2)}
                  </Text>
                </View>
              </View>
            ))}
            {/* Discount row */}
            <View style={styles.discountRow}>
              <View
                style={{
                  flex: 76,
                  borderRightWidth: 1,
                  borderColor: "#000",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  paddingRight: 12,
                }}
              >
                <Text style={styles.discountText}>Referral Discount</Text>
              </View>
              <View
                style={{
                  flex: 16,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text style={styles.discountAmountText}>
                  {(order.discount_applied)}
                </Text>
              </View>
            </View>

            {/* Shipping charge row */}
            <View style={styles.shippingChargeRow}>
              <View
                style={{
                  flex: 76,
                  borderRightWidth: 1,
                  borderColor: "#000",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  paddingRight: 12,
                }}
              >
                <Text style={styles.shippingChargeText}>Shipping Charge</Text>
              </View>
              <View
                style={{
                  flex: 16,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text style={styles.shippingChargeAmountText}>
                  {order.shipping_charge}
                </Text>
              </View>
            </View>

            {/* TOTAL ROW */}
            <View style={styles.totalRow}>
              <View
                style={{
                  flex: 76,
                  borderRightWidth: 1,
                  borderColor: "#000",
                  justifyContent: "center",
                  alignItems: "flex-end",
                  paddingRight: 12,
                }}
              >
                <Text style={styles.totalPayableText}>Total Payable</Text>
              </View>
              <View
                style={{
                  flex: 16,
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Text style={styles.totalAmountText}>
                  {order.total_amount}
                </Text>
              </View>
            </View>
          </View>

          {/* ── RIGHT: "Total Paid" spanning column ── */}
          <View
            style={{
              flex: 16,
              borderLeftWidth: 1,
              borderColor: "#000",
            }}
          >
            {/* Header cell — same height as tableHeader */}
            <View
              style={{
                height: 25,
                borderBottomWidth: 1,
                borderColor: "#000",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text style={styles.headerTitle}>Total Paid</Text>
            </View>

            {/* Spanning value — fills remaining height */}
            <View
              style={{
                flex: 1,
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Text style={styles.bigTotalAmount}>
                {Number(order.total_amount).toFixed(2)}
              </Text>
            </View>
          </View>
        </View>
      </View>

      {/* FOOTER */}
      <View style={styles.footer} wrap={false}>
        <Text style={styles.footerHeading}>
          Protein is vital to build and repair body tissue and fight viral and
          bacterial infections. BOOST YOUR IMMUNITY WITH PROTEIN POWDER
        </Text>

        <Text
          style={{
            fontSize: 8,
            fontWeight: "bold",
            marginBottom: 4,
          }}
        >
          Terms & Conditions of this Order
        </Text>

        <View style={styles.termsWrapper}>
          <View style={styles.termsColumn}>
            <Text style={styles.term}>
              1. Seller is not responsible for any loss or damage of goods in
              transit.
            </Text>

            <Text style={styles.term}>
              2. Products once sold shall not be eligible for return or refund.
            </Text>

            <Text style={styles.term}>
              3. Please store products at cool place and away from sunlight.
            </Text>
          </View>

          <View style={styles.termsColumn}>
            <Text style={styles.term}>
              4. Any inaccuracy in this bill must be notified immediately on its
              receipt.
            </Text>

            <Text style={styles.term}>
              5. Disputes if any will be subject to Guwahati High Court
              jurisdiction.
            </Text>
          </View>
        </View>

        <Text style={styles.suggestion}>
          For suggestions, contact your Health & Wellness Expert :-
          <Text style={styles.suggestionBlue}>
            {" "}
            {order.referred_by_name
              ? `${order.referred_by_name} (${order.referred_by_phone})`
              : "N/A"}
          </Text>
        </Text>

        <View style={styles.signatureSection}>
          <Text style={styles.signatureLine1}>
            for National Healthcare & Wellness Mission
          </Text>
          {/* change logo to signature */}
          <Image style={styles.signatureImage} src={signature} />

          <Text style={styles.signatureName}>
            Authorized Signatory : DR MONIDEEP BORUAH
          </Text>

          <Text style={styles.preparedBy}>Bill Prepared By : System</Text>
        </View>
      </View>
    </Page>
  </Document>
);